import {
  AggregateFunction,
  CountOperator,
  DateUnit,
  RelativeDateDirection,
  type RelatedResourceAggregate,
  type RelatedResourceCondition,
  type RelativeDate,
} from '@reach/shared-types/conditions';
import type { FieldInfo } from '@reach/shared-types/segment-schema-graph';

const NUMERIC_ONLY_FUNCTIONS: ReadonlySet<AggregateFunction> = new Set([
  AggregateFunction.SUM,
  AggregateFunction.AVG,
]);

export function aggregatableFields(
  fields: FieldInfo[],
  aggregateFunction: AggregateFunction,
): FieldInfo[] {
  const acceptsDates = !NUMERIC_ONLY_FUNCTIONS.has(aggregateFunction);
  return fields.filter(field => field.type === 'number' || (acceptsDates && field.type === 'date'));
}

export function aggregateTargetsCompatibleField(
  aggregate: RelatedResourceAggregate,
  fields: FieldInfo[],
): boolean {
  const field = aggregatableFields(fields, aggregate.function).find(
    candidate => candidate.name === aggregate.field,
  );
  if (!field) return false;
  const comparesNumbers = typeof aggregate.value === 'number';
  const expectedFieldType = comparesNumbers ? 'number' : 'date';
  return field.type === expectedFieldType;
}

const DEFAULT_RELATIVE_DATE: RelativeDate = {
  type: 'relative',
  value: 30,
  unit: DateUnit.DAYS,
  direction: RelativeDateDirection.BEFORE,
};

export const defaultValueForField = (field: FieldInfo | undefined): RelatedResourceAggregate['value'] =>
  field?.type === 'date' ? DEFAULT_RELATIVE_DATE : 0;

export function createDefaultAggregate(fields: FieldInfo[]): RelatedResourceAggregate | undefined {
  const numericField = aggregatableFields(fields, AggregateFunction.SUM)[0];
  if (numericField) {
    return {
      function: AggregateFunction.SUM,
      field: numericField.name,
      operator: CountOperator.GT,
      value: 0,
    };
  }
  const dateField = aggregatableFields(fields, AggregateFunction.MAX)[0];
  if (dateField) {
    return {
      function: AggregateFunction.MAX,
      field: dateField.name,
      operator: CountOperator.LT,
      value: DEFAULT_RELATIVE_DATE,
    };
  }
  return undefined;
}

export function isAtLeastOneRecord(
  condition: Pick<RelatedResourceCondition, 'countOperator' | 'countValue'>,
): boolean {
  return condition.countOperator === CountOperator.GTE && condition.countValue === 1;
}
